import React from 'react';
import { Sparkles, AlertTriangle, TrendingUp, ShieldAlert, Briefcase, Building2 } from 'lucide-react';
import { resetScrollToTop, useScrollToTop } from '../../../../utils/scrollUtils';

const GoNoGoResults = ({
    goNoGoData,
    activeTab,
    setActiveTab,
    handleGoNoGoSubmit,
    updatingDecision,
    selectedDecision
}) => {
    // Automatically reset scroll position to top whenever active tab changes
    useScrollToTop([activeTab]);

    const report = goNoGoData?.go_no_go_report || {};
    const decision = report?.go_no_go_decision || {};
    const clientInfo = report?.client_information || {};
    const strengths = report?.strengths || [];
    const risks = report?.risks || [];
    const similarProjects = report?.similar_projects || [];
    const commercialSummary = report?.commercial_summary || {};

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        resetScrollToTop();
    };

    return (
        <div className="go-no-go-results">
            <div className="go-no-go-tabs">

                <button
                    className={`gng-tab-btn ${activeTab === 'client-info' ? 'active' : ''}`}
                    onClick={() => handleTabChange('client-info')}
                >
                    <Building2 size={16} />
                    <span>Client Information</span>
                </button>
                <button
                    className={`gng-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => handleTabChange('overview')}
                >
                    <TrendingUp size={16} />
                    <span>Decision Overview</span>
                </button>

                <button
                    className={`gng-tab-btn ${activeTab === 'strengths-risks' ? 'active' : ''}`}
                    onClick={() => handleTabChange('strengths-risks')}
                >
                    <ShieldAlert size={16} />
                    <span>Strengths & Risks</span>
                </button>
                <button
                    className={`gng-tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
                    onClick={() => handleTabChange('projects')}
                >
                    <Briefcase size={16} />
                    <span>Similar Projects</span>
                </button>
            </div>

            <div className="go-no-go-tab-content">
                {activeTab === 'overview' && (
                    <div className="gng-tab-pane">
                        {String(decision.recommendation).toUpperCase() === 'GO' ? (
                            <div className="decision-banner go">
                                <div className="banner-icon-box go"><Sparkles size={24} /></div>
                                <div className="banner-details">
                                    <div className="recommendation-badge go">GO RECOMMENDATION</div>
                                    <h3>AI Recommendation: GO</h3>
                                    <p>Confidence Level: <strong>{decision.confidence_level || 'Medium'}</strong></p>
                                </div>
                            </div>
                        ) : (
                            <div className="decision-banner nogo">
                                <div className="banner-icon-box nogo"><AlertTriangle size={24} /></div>
                                <div className="banner-details">
                                    <div className="recommendation-badge nogo">NO-GO RECOMMENDATION</div>
                                    <h3>AI Recommendation: NO-GO</h3>
                                    <p>Confidence Level: <strong>{decision.confidence_level || 'Medium'}</strong></p>
                                </div>
                            </div>
                        )}

                        <div className="gng-card-redesign">
                            <h4>Decision Rationale</h4>
                            <p className="rationale-text">{decision.decision_rationale}</p>
                        </div>

                        <div className="gng-grid-2col">
                            <div className="gng-card-redesign win-themes-card">
                                <h4>🏆 Key Win Themes</h4>
                                <ul>
                                    {(decision.key_win_themes || []).map((theme, i) => <li key={i}>{theme}</li>)}
                                </ul>
                            </div>
                            <div className="gng-card-redesign red-flags-card">
                                <h4>⚠️ Red Flags</h4>
                                <ul>
                                    {(decision.red_flags || []).map((flag, i) => <li key={i}>{flag}</li>)}
                                </ul>
                            </div>
                        </div>

                        <div className="gng-card-redesign">
                            <h4>Commercial Summary</h4>
                            <div className="commercial-details-row">
                                <div className="comm-detail-item">
                                    <span className="comm-label">Deal Value:</span>
                                    <strong className="comm-val">{commercialSummary.deal_value || 'Not Specified'}</strong>
                                </div>
                                <div className="comm-detail-item">
                                    <span className="comm-label">Penalty Risk:</span>
                                    <strong className={`comm-val penalty-${commercialSummary.penalty_risk ? 'yes' : 'no'}`}>
                                        {commercialSummary.penalty_risk ? 'Yes' : 'No'}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'client-info' && (
                    <div className="gng-tab-pane animate-fade-in">
                        <div className="gng-grid-2col" style={{ marginBottom: '1.25rem' }}>
                            <div className="gng-card-redesign" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: 0 }}>
                                <h4>🏢 Client Details</h4>
                                <div className="client-detail-item-box">
                                    <span className="client-detail-label">Client Name</span>
                                    <span className="client-detail-value">{clientInfo.client_name || 'Not Specified'}</span>
                                </div>
                                <div className="client-detail-item-box">
                                    <span className="client-detail-label">Location</span>
                                    <span className="client-detail-value">{clientInfo.client_location || 'Not Specified'}</span>
                                </div>
                            </div>
                            <div className="gng-card-redesign" style={{ marginBottom: 0 }}>
                                <h4>🎯 Client Vision</h4>
                                <p className="rationale-text" style={{ fontStyle: 'italic', color: '#475569', lineHeight: '1.6' }}>
                                    "{clientInfo.client_vision || 'Not Specified'}"
                                </p>
                            </div>
                        </div>

                        <div className="gng-card-redesign" style={{ marginBottom: '1.25rem' }}>
                            <h4>🏢 About the Client</h4>
                            <p className="rationale-text" style={{ lineHeight: '1.6' }}>{clientInfo.about_client || 'Not Specified'}</p>
                        </div>

                        <div className="gng-card-redesign" style={{ marginBottom: '1.25rem' }}>
                            <h4>📝 About the Project</h4>
                            <p className="rationale-text" style={{ lineHeight: '1.6' }}>{clientInfo.about_the_project || 'Not Specified'}</p>
                        </div>

                        <div className="gng-grid-2col" style={{ marginBottom: 0 }}>
                            <div className="gng-card-redesign win-themes-card" style={{ marginBottom: 0 }}>
                                <h4>💥 Current Pain Points</h4>
                                <ul className="pain-points-list">
                                    {(clientInfo.current_pain_points || []).map((point, i) => (
                                        <li key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.82rem', color: '#E11D48', lineHeight: '1.4' }}>
                                            <span style={{ fontWeight: 'bold' }}>•</span>
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                    {(!clientInfo.current_pain_points || clientInfo.current_pain_points.length === 0) && (
                                        <li style={{ color: '#64748B', fontSize: '0.82rem' }}>No pain points listed.</li>
                                    )}
                                </ul>
                            </div>
                            <div className="gng-card-redesign red-flags-card" style={{ marginBottom: 0 }}>
                                <h4>✨ Key Features</h4>
                                <ul className="key-features-list">
                                    {(clientInfo.key_features || []).map((feature, i) => (
                                        <li key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.82rem', color: '#059669', lineHeight: '1.4' }}>
                                            <span style={{ fontWeight: 'bold' }}>✓</span>
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                    {(!clientInfo.key_features || clientInfo.key_features.length === 0) && (
                                        <li style={{ color: '#64748B', fontSize: '0.82rem' }}>No key features listed.</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'strengths-risks' && (
                    <div className="gng-tab-pane">
                        <div className="gng-card-redesign">
                            <h4>Strengths & Capabilities</h4>
                            <ul className="strengths-list-elegant">
                                {strengths.map((str, i) => (
                                    <li key={i}>
                                        <span className="bullet-check">✓</span>
                                        <span className="strength-text">{str}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="gng-card-redesign">
                            <h4>Risk Matrix</h4>
                            <div className="table-responsive">
                                <table className="gng-risks-table">
                                    <thead>
                                        <tr><th>Risk Area</th><th>Severity</th><th>Details</th></tr>
                                    </thead>
                                    <tbody>
                                        {risks.map((risk, i) => (
                                            <tr key={i}>
                                                <td className="risk-area-cell">{risk.area}</td>
                                                <td><span className={`risk-severity-badge ${String(risk.severity).toLowerCase()}`}>{risk.severity}</span></td>
                                                <td className="risk-detail-cell">{risk.detail}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'projects' && (
                    <div className="gng-tab-pane">
                        <h4>Similar Historical Projects</h4>
                        <div className="similar-projects-grid">
                            {similarProjects.map((proj, i) => (
                                <div key={i} className="project-relevance-card">
                                    <div className="proj-card-header">
                                        <h5>{proj.project_name}</h5>
                                        <span className="relevance-score">{proj.relevance_percentage}% Relevant</span>
                                    </div>
                                    <div className="proj-badge-sector">{proj.sector}</div>
                                    <div className="relevance-progress-bar">
                                        <div className="relevance-fill" style={{ width: `${proj.relevance_percentage}%` }}></div>
                                    </div>
                                    <p className="why-matters-text"><strong>Why it matters:</strong> {proj.why_it_matters}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="gng-decision-action-footer">
                <button className="gng-action-btn gng-btn-no-go" onClick={() => handleGoNoGoSubmit(9)} disabled={updatingDecision}>
                    {updatingDecision && selectedDecision === 9 ? 'Processing...' : 'No-Go (Decline Bid)'}
                </button>
                <button className="gng-action-btn gng-btn-go" onClick={() => handleGoNoGoSubmit(8)} disabled={updatingDecision}>
                    {updatingDecision && selectedDecision === 8 ? 'Processing...' : 'Go (Proceed with Bid)'}
                </button>
            </div>
        </div>
    );
};

export default GoNoGoResults;
